import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
  Box, Typography, Button, Stack, Paper, TextField, Divider,
  IconButton, Tooltip, Chip, Alert, useTheme, Slider, Select,
  MenuItem, FormControl, InputLabel, Switch, FormControlLabel,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import RouteIcon from '@mui/icons-material/Route';
import ExploreIcon from '@mui/icons-material/Explore';
import TuneIcon from '@mui/icons-material/Tune';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import CheckIcon from '@mui/icons-material/Check';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { haversineDistance, calculateBearing, parseAnyGeoFile, exportGeoJSON, readFileAsText } from '../../utils/geoUtils';
import SEO, { createSoftwareAppSchema } from '../../components/SEO';
import 'leaflet/dist/leaflet.css';
import 'leaflet-fullscreen/dist/leaflet.fullscreen.css';
import L from 'leaflet';
import 'leaflet-fullscreen';

// ── Leaflet icon fix ────────────────────────────────────────────────────────
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
let DefaultIcon = L.icon({ iconUrl: icon, shadowUrl: iconShadow, iconAnchor: [12, 41] });
L.Marker.prototype.options.icon = DefaultIcon;

// ── Constants ───────────────────────────────────────────────────────────────
const ZERO_REF_PRESETS = [
  { label: 'North (Geographic)', value: 0, description: '0° = True North (standard compass)' },
  { label: 'East (Math/Screen)', value: 270, description: '0° = East (counter-clockwise from N = +90)' },
  { label: 'South', value: 180, description: '0° = South' },
  { label: 'West', value: 90, description: '0° = West' },
  { label: 'Custom…', value: 'custom', description: 'Set any custom zero reference angle' },
];

// Convert a "raw" heading (in user's reference frame) to a geographic bearing
// zeroRefDeg: how many degrees clockwise from N does the user's 0 point to?
// e.g. zeroRef=0 → North convention, zeroRef=270 → East convention (East is 90° from N, so 0°E = 90°N → offset = -90 = +270)
const rawHeadingToGeoBearing = (rawHeading, zeroRefDeg, applyMathTransform = false) => {
  let hdg = rawHeading;
  if (applyMathTransform) {
    hdg = 360 - Math.abs(90 - hdg);
  }
  return ((hdg + zeroRefDeg) % 360 + 360) % 360;
};

// Convert a geographic bearing to the user's heading system
const geoBearingToRaw = (geoBearing, zeroRefDeg) => {
  return ((geoBearing - zeroRefDeg) % 360 + 360) % 360;
};

// Create an SVG arrow icon for a given bearing and color
const createArrowIcon = (bearingDeg, color = '#6366f1', size = 32) => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="-${size/2} -${size/2} ${size} ${size}">
    <g transform="rotate(${bearingDeg})">
      <polygon points="0,-${size*0.38} ${size*0.16},${size*0.22} 0,${size*0.12} -${size*0.16},${size*0.22}" 
               fill="${color}" stroke="white" stroke-width="1.5" stroke-linejoin="round"/>
    </g>
  </svg>`;
  return L.divIcon({
    html: svg,
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

// Create a start/end marker icon
const createCircleIcon = (color, label) => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">
    <circle cx="14" cy="14" r="11" fill="${color}" stroke="white" stroke-width="2.5"/>
    <text x="14" y="19" text-anchor="middle" font-size="12" font-weight="bold" fill="white" font-family="sans-serif">${label}</text>
  </svg>`;
  return L.divIcon({ html: svg, className: '', iconSize: [28, 28], iconAnchor: [14, 14] });
};

// ── Performance constants ─────────────────────────────────────────────────────
const LARGE_DATASET_THRESHOLD = 50;   // above this: simplified markers
const MAX_ROUTE_ARROWS = 60;          // hard cap on direction arrows
const MAP_DEBOUNCE_MS = 300;          // ms to debounce map redraws

// ── Route Drawer Map Component ───────────────────────────────────────────────
function RouteDrawerMap({ waypoints, zeroRefDeg, applyMathTransform, showHeadingArrows, showRouteArrows, arrowSpacing, mapId = 'route-drawer-map' }) {
  const mapRef = useRef(null);
  const layersRef = useRef([]);
  const debounceRef = useRef(null);

  // Init map
  useEffect(() => {
    if (mapRef.current) return;
    const map = L.map(mapId, {
      fullscreenControl: true,
      fullscreenControlOptions: { position: 'topright' },
    }).setView([28.6139, 77.2090], 5);

    const osmLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    });
    const darkLayer = L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; CartoDB',
    });
    const satelliteLayer = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
      attribution: '&copy; Esri',
    });

    osmLayer.addTo(map);
    L.control.layers({ 'Streets': osmLayer, 'Dark': darkLayer, 'Satellite': satelliteLayer }, null, { position: 'topright' }).addTo(map);
    L.control.scale({ position: 'bottomleft', imperial: false }).addTo(map);
    mapRef.current = map;

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapId]);

  // Debounced redraw
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const map = mapRef.current;
      if (!map) return;

      // Remove old layers
      layersRef.current.forEach(l => { try { map.removeLayer(l); } catch (e) {} });
      layersRef.current = [];

      const validWPs = waypoints.filter(wp => {
        const lat = parseFloat(wp.lat);
        const lon = parseFloat(wp.lon);
        return !isNaN(lat) && !isNaN(lon) && lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180;
      });

      if (validWPs.length === 0) return;

      const isLargeDataset = validWPs.length > LARGE_DATASET_THRESHOLD;
      const coords = validWPs.map(wp => [parseFloat(wp.lon), parseFloat(wp.lat)]);

      // ── Draw route line ──
      if (coords.length >= 2) {
        const latLngs = coords.map(([lon, lat]) => [lat, lon]);
        const routeLine = L.polyline(latLngs, {
          color: '#6366f1',
          weight: isLargeDataset ? 2.5 : 3,
          opacity: 0.85,
        }).addTo(map);
        layersRef.current.push(routeLine);

        // ── Route direction arrows — capped at MAX_ROUTE_ARROWS ──
        if (showRouteArrows) {
          const totalDist = coords.reduce((sum, c, i) => i === 0 ? 0 : sum + haversineDistance(coords[i - 1], c), 0);
          // Dynamically adjust spacing so we never exceed MAX_ROUTE_ARROWS
          const effectiveSpacing = Math.max(arrowSpacing, totalDist / MAX_ROUTE_ARROWS);
          let cumDist = 0;
          let nextArrowAt = effectiveSpacing;
          let arrowCount = 0;

          for (let i = 1; i < coords.length && arrowCount < MAX_ROUTE_ARROWS; i++) {
            const segDist = haversineDistance(coords[i - 1], coords[i]);
            const segBearing = calculateBearing(coords[i - 1], coords[i]);
            const endCum = cumDist + segDist;

            while (nextArrowAt <= endCum && nextArrowAt <= totalDist && arrowCount < MAX_ROUTE_ARROWS) {
              const t = (nextArrowAt - cumDist) / segDist;
              const lon = coords[i - 1][0] + t * (coords[i][0] - coords[i - 1][0]);
              const lat = coords[i - 1][1] + t * (coords[i][1] - coords[i - 1][1]);
              const arrowMarker = L.marker([lat, lon], {
                icon: createArrowIcon(segBearing, '#6366f1', 22),
                interactive: false,
              }).addTo(map);
              layersRef.current.push(arrowMarker);
              nextArrowAt += effectiveSpacing;
              arrowCount++;
            }
            cumDist = endCum;
          }
        }
      }

      // ── Draw waypoint markers ──
      // For large datasets: only draw start, end, and sampled heading points
      const indicesToDraw = isLargeDataset
        ? new Set([0, validWPs.length - 1]) // only start + end
        : new Set(validWPs.map((_, i) => i));

      // If headings are present in large dataset, sample up to 40 evenly
      if (isLargeDataset && showHeadingArrows) {
        const hasHeadings = validWPs.some(wp => wp.heading !== '');
        if (hasHeadings) {
          const step = Math.max(1, Math.floor(validWPs.length / 40));
          for (let i = 0; i < validWPs.length; i += step) indicesToDraw.add(i);
        }
      }

      validWPs.forEach((wp, i) => {
        if (!indicesToDraw.has(i)) return;

        const lat = parseFloat(wp.lat);
        const lon = parseFloat(wp.lon);
        const isFirst = i === 0;
        const isLast = i === validWPs.length - 1;

        let geoBearing = null;
        const rawHeading = parseFloat(wp.heading);
        if (!isNaN(rawHeading)) {
          geoBearing = rawHeadingToGeoBearing(rawHeading, zeroRefDeg, applyMathTransform);
        } else if (coords.length >= 2) {
          geoBearing = i < coords.length - 1
            ? calculateBearing(coords[i], coords[i + 1])
            : calculateBearing(coords[i - 1], coords[i]);
        }

        const color = isFirst ? '#10b981' : isLast ? '#f43f5e' : '#6366f1';
        const hasExplicitHeading = !isNaN(parseFloat(wp.heading));

        if (showHeadingArrows && geoBearing !== null && (hasExplicitHeading || (!isLargeDataset && coords.length >= 2))) {
          const rawDisplay = hasExplicitHeading ? parseFloat(wp.heading).toFixed(1) : geoBearing.toFixed(1);
          const arrowMarker = L.marker([lat, lon], {
            icon: createArrowIcon(geoBearing, '#f59e0b', isLargeDataset ? 32 : 56),
          });
          arrowMarker.bindPopup(
            `<b>${wp.label || `WP ${i + 1}`}</b><br/>` +
            `Lat: ${lat.toFixed(6)}, Lon: ${lon.toFixed(6)}<br/>` +
            `Heading: ${rawDisplay}°<br/>Geo Bearing: ${geoBearing.toFixed(1)}° from N`
          );
          arrowMarker.addTo(map);
          layersRef.current.push(arrowMarker);
        } else if (isFirst || isLast) {
          const circleM = L.marker([lat, lon], {
            icon: isFirst ? createCircleIcon('#10b981', 'S') : createCircleIcon('#f43f5e', 'E'),
          });
          circleM.bindPopup(
            `<b>${wp.label || (isFirst ? 'Start' : 'End')}</b><br/>Lat: ${lat.toFixed(6)}, Lon: ${lon.toFixed(6)}`
          );
          circleM.addTo(map);
          layersRef.current.push(circleM);
        } else if (!isLargeDataset) {
          const circleM = L.marker([lat, lon], {
            icon: createCircleIcon('#6366f1', (i + 1).toString()),
          });
          circleM.bindPopup(
            `<b>${wp.label || `WP ${i + 1}`}</b><br/>Lat: ${lat.toFixed(6)}, Lon: ${lon.toFixed(6)}`
          );
          circleM.addTo(map);
          layersRef.current.push(circleM);
        }
      });

      // Fit bounds
      if (validWPs.length === 1) {
        map.setView([parseFloat(validWPs[0].lat), parseFloat(validWPs[0].lon)], 14);
      } else if (validWPs.length >= 2) {
        const bounds = L.latLngBounds(coords.map(([lon, lat]) => [lat, lon]));
        if (bounds.isValid()) map.fitBounds(bounds, { padding: [50, 50] });
      }
    }, MAP_DEBOUNCE_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waypoints, zeroRefDeg, applyMathTransform, showHeadingArrows, showRouteArrows, arrowSpacing]);

  return <div id={mapId} style={{ height: '100%', width: '100%' }} />;
}

// ── Compass Rose Widget ──────────────────────────────────────────────────────
function CompassRose({ zeroRefDeg }) {
  const size = 120;
  const cx = size / 2;
  const cy = size / 2;
  const r = size * 0.42;
  // The zero marker rotates to show where "0" is in geographic terms
  const zeroAngleRad = (zeroRefDeg - 90) * (Math.PI / 180); // -90 because SVG 0° is East
  const zeroX = cx + r * Math.cos(zeroAngleRad);
  const zeroY = cy + r * Math.sin(zeroAngleRad);

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: 'block' }}>
      {/* Outer circle */}
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#6366f1" strokeWidth="1.5" opacity="0.3" />
      {/* Tick marks for N/E/S/W */}
      {[
        { deg: 0, label: 'N', color: '#ef4444' },
        { deg: 90, label: 'E', color: '#94a3b8' },
        { deg: 180, label: 'S', color: '#94a3b8' },
        { deg: 270, label: 'W', color: '#94a3b8' },
      ].map(({ deg, label, color }) => {
        const rad = (deg - 90) * (Math.PI / 180);
        const x1 = cx + (r - 8) * Math.cos(rad);
        const y1 = cy + (r - 8) * Math.sin(rad);
        const x2 = cx + r * Math.cos(rad);
        const y2 = cy + r * Math.sin(rad);
        const tx = cx + (r + 10) * Math.cos(rad);
        const ty = cy + (r + 10) * Math.sin(rad);
        return (
          <g key={deg}>
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth="2" />
            <text x={tx} y={ty} textAnchor="middle" dominantBaseline="middle" fill={color} fontSize="11" fontWeight="bold">{label}</text>
          </g>
        );
      })}
      {/* Zero reference arrow */}
      <line
        x1={cx} y1={cy}
        x2={zeroX} y2={zeroY}
        stroke="#f59e0b" strokeWidth="2.5" strokeLinecap="round"
      />
      {/* Arrow head */}
      <polygon
        points={`${zeroX},${zeroY} ${zeroX - 4 * Math.sin(zeroAngleRad + 0.4)},${zeroY + 4 * Math.cos(zeroAngleRad + 0.4)} ${zeroX - 4 * Math.sin(zeroAngleRad - 0.4)},${zeroY + 4 * Math.cos(zeroAngleRad - 0.4)}`}
        fill="#f59e0b"
      />
      {/* Center dot */}
      <circle cx={cx} cy={cy} r="3" fill="#6366f1" />
      {/* Zero label */}
      <text x={zeroX + 5 * Math.cos(zeroAngleRad)} y={zeroY + 5 * Math.sin(zeroAngleRad)}
        textAnchor="middle" dominantBaseline="middle" fill="#f59e0b" fontSize="10" fontWeight="bold">0°</text>
    </svg>
  );
}

// ── Waypoints Table Component ──────────────────────────────────────────────────
function WaypointsTable({ waypoints, selectedPreset, updateWaypoint, removeWaypoint }) {
  const isLargeDataset = waypoints.length > LARGE_DATASET_THRESHOLD;
  const [editingIdx, setEditingIdx] = useState(-1);

  return (
    <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 1, pb: 1 }}>
      <TableContainer>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 0.5, px: 0.5 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 0.5, px: 0.5 }}>Label</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 0.5, px: 0.5 }}>Lat</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 0.5, px: 0.5 }}>Lon</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 0.5, px: 0.5 }}>
                <Tooltip title={`Heading in your chosen reference (0° = ${selectedPreset.label})`}>
                  <span>Hdg°</span>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ py: 0.5, px: 0.5 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {waypoints.map((wp, i) => {
              const isEditing = !isLargeDataset || editingIdx === i;

              return (
                <TableRow key={i} sx={{ '& td': { py: 0.3 }, cursor: isLargeDataset && !isEditing ? 'pointer' : 'default', '&:hover': { bgcolor: isLargeDataset && !isEditing ? 'action.hover' : 'inherit' } }} onClick={() => { if (isLargeDataset && !isEditing) setEditingIdx(i); }}>
                  <TableCell sx={{ px: 0.5 }}>
                    <Chip
                      label={i + 1}
                      size="small"
                      sx={{
                        height: 20, width: 26, fontSize: '0.65rem', fontWeight: 700,
                        bgcolor: i === 0 ? '#10b98133' : i === waypoints.length - 1 ? '#f43f5e33' : '#6366f133',
                        color: i === 0 ? '#10b981' : i === waypoints.length - 1 ? '#f43f5e' : '#6366f1',
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ px: 0.5 }}>
                    {isEditing ? (
                      <TextField
                        value={wp.label}
                        onChange={e => updateWaypoint(i, 'label', e.target.value)}
                        size="small"
                        variant="standard"
                        placeholder={`WP ${i + 1}`}
                        inputProps={{ style: { fontSize: '0.75rem', padding: '2px 0' } }}
                        sx={{ width: 60 }}
                        autoFocus={isLargeDataset}
                        onBlur={() => { if (isLargeDataset) setEditingIdx(-1); }}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ fontSize: '0.75rem', width: 60, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{wp.label}</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ px: 0.5 }}>
                    {isEditing ? (
                      <TextField
                        value={wp.lat}
                        onChange={e => updateWaypoint(i, 'lat', e.target.value)}
                        size="small"
                        variant="standard"
                        placeholder="Lat"
                        inputProps={{ style: { fontSize: '0.75rem', padding: '2px 0' } }}
                        error={wp.lat !== '' && isNaN(parseFloat(wp.lat))}
                        sx={{ width: 75 }}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{wp.lat}</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ px: 0.5 }}>
                    {isEditing ? (
                      <TextField
                        value={wp.lon}
                        onChange={e => updateWaypoint(i, 'lon', e.target.value)}
                        size="small"
                        variant="standard"
                        placeholder="Lon"
                        inputProps={{ style: { fontSize: '0.75rem', padding: '2px 0' } }}
                        error={wp.lon !== '' && isNaN(parseFloat(wp.lon))}
                        sx={{ width: 75 }}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{wp.lon}</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ px: 0.5 }}>
                    {isEditing ? (
                      <TextField
                        value={wp.heading}
                        onChange={e => updateWaypoint(i, 'heading', e.target.value)}
                        size="small"
                        variant="standard"
                        placeholder="—"
                        inputProps={{ style: { fontSize: '0.75rem', padding: '2px 0' } }}
                        sx={{ width: 48 }}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{wp.heading || '—'}</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ px: 0.25 }}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); removeWaypoint(i); }} sx={{ p: 0.25, color: 'error.main' }}>
                      <DeleteIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ── Main Route Drawer Component ──────────────────────────────────────────────
const DEFAULT_WAYPOINT = () => ({ lat: '', lon: '', heading: '', label: '' });

function RouteDrawer() {
  const theme = useTheme();
  const [waypoints, setWaypoints] = useState([
    { lat: '28.6139', lon: '77.2090', heading: '45', label: 'Start' },
    { lat: '28.6500', lon: '77.2500', heading: '90', label: 'WP 2' },
    { lat: '28.7000', lon: '77.3000', heading: '', label: 'End' },
  ]);
  const [zeroRefPreset, setZeroRefPreset] = useState(0);   // index into ZERO_REF_PRESETS
  const [customZeroRef, setCustomZeroRef] = useState(0);    // degrees for custom
  const [applyMathTransform, setApplyMathTransform] = useState(false);
  const [showHeadingArrows, setShowHeadingArrows] = useState(true);
  const [showRouteArrows, setShowRouteArrows] = useState(true);
  const [arrowSpacing, setArrowSpacing] = useState(500); // meters
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [showPasteArea, setShowPasteArea] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pasteMode, setPasteMode] = useState('append'); // 'append' | 'replace'
  const [pastePreview, setPastePreview] = useState(null); // parsed preview

  const selectedPreset = ZERO_REF_PRESETS[zeroRefPreset];
  const zeroRefDeg = selectedPreset.value === 'custom' ? customZeroRef : selectedPreset.value;

  // ── Waypoint editing ──────────────────────────────────────────────────────
  const updateWaypoint = (idx, field, value) => {
    setWaypoints(prev => prev.map((wp, i) => i === idx ? { ...wp, [field]: value } : wp));
  };

  const addWaypoint = () => {
    setWaypoints(prev => [...prev, DEFAULT_WAYPOINT()]);
  };

  const removeWaypoint = (idx) => {
    setWaypoints(prev => prev.filter((_, i) => i !== idx));
  };

  const moveWaypoint = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= waypoints.length) return;
    setWaypoints(prev => {
      const arr = [...prev];
      [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
      return arr;
    });
  };

  // ── Import from file / paste ──────────────────────────────────────────────
  const handleImport = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const text = await readFileAsText(file);
      const result = parseAnyGeoFile(text);
      if (!result.geoJson) throw new Error(result.error || 'Could not parse file');

      const newWPs = [];
      result.geoJson.features.forEach((f, fi) => {
        if (f.geometry?.type === 'Point') {
          const [lon, lat] = f.geometry.coordinates;
          const heading = f.properties?.heading ?? f.properties?.bearing ?? f.properties?.course ?? '';
          newWPs.push({
            lat: lat.toFixed(6),
            lon: lon.toFixed(6),
            heading: heading !== '' ? String(heading) : '',
            label: f.properties?.name ?? f.properties?.label ?? `WP ${fi + 1}`,
          });
        } else if (f.geometry?.type === 'LineString') {
          f.geometry.coordinates.forEach((coord, ci) => {
            newWPs.push({
              lat: coord[1].toFixed(6),
              lon: coord[0].toFixed(6),
              heading: '',
              label: `WP ${ci + 1}`,
            });
          });
        }
      });

      if (newWPs.length === 0) throw new Error('No point or line features found');
      setWaypoints(newWPs);
      setError('');
    } catch (err) {
      setError(`Import failed: ${err.message}`);
    }
  }, []);

  // ── Paste text parser ─────────────────────────────────────────────────────
  // Accepts lines like:
  //   lat, lon                      → no heading
  //   lat, lon, heading             → with heading
  //   lat, lon, heading, label      → with heading + label
  //   lat\tlon\theading             → tab-separated
  //   lat lon heading               → space-separated
  // Also accepts JSON arrays: [[lat,lon],[lat,lon,hdg],...]
  const parsePasteText = useCallback((text) => {
    if (!text.trim()) return null;

    // ── Try JSON array ──
    if (text.trim().startsWith('[')) {
      try {
        const arr = JSON.parse(text.trim());
        if (Array.isArray(arr)) {
          const wps = arr.map((item, i) => {
            // item can be [lat, lon], [lat, lon, hdg], or {lat, lon, heading, label}
            if (Array.isArray(item) && item.length >= 2) {
              return {
                lat: String(item[0]),
                lon: String(item[1]),
                heading: item[2] != null ? String(item[2]) : '',
                label: item[3] ? String(item[3]) : `WP ${i + 1}`,
              };
            } else if (typeof item === 'object' && item !== null) {
              const lat = item.lat ?? item.latitude ?? item.y ?? '';
              const lon = item.lon ?? item.lng ?? item.longitude ?? item.x ?? '';
              const hdg = item.heading ?? item.bearing ?? item.course ?? item.hdg ?? '';
              const lbl = item.label ?? item.name ?? item.id ?? `WP ${i + 1}`;
              return { lat: String(lat), lon: String(lon), heading: String(hdg), label: String(lbl) };
            }
            return null;
          }).filter(Boolean);
          if (wps.length > 0) return wps;
        }
      } catch (e) { /* fall through */ }
    }

    // ── Try line-by-line text ──
    const lines = text.trim().split('\n').filter(l => l.trim());
    if (lines.length === 0) return null;

    // Detect separator from first data line
    const firstLine = lines[0].trim();
    let sep;
    if (firstLine.includes('\t'))      sep = /\t/;
    else if (firstLine.includes(';'))  sep = /\s*;\s*/;
    else if (firstLine.includes('|'))  sep = /\s*\|\s*/;
    else if (firstLine.includes(','))  sep = /\s*,\s*/;
    else                               sep = /\s+/;

    // Check if first line is a header (contains non-numeric text in position 0 or 1)
    const firstParts = firstLine.split(sep);
    const isHeader = isNaN(parseFloat(firstParts[0])) || isNaN(parseFloat(firstParts[1]));

    // If it's a header, detect column roles
    let latCol = 0, lonCol = 1, hdgCol = 2, lblCol = 3;
    if (isHeader) {
      const headers = firstParts.map(h => h.trim().toLowerCase().replace(/[^a-z0-9]/g, ''));
      const LAT_KEYS = ['lat', 'latitude', 'y'];
      const LON_KEYS = ['lon', 'lng', 'longitude', 'x'];
      const HDG_KEYS = ['heading', 'bearing', 'course', 'hdg', 'azimuth', 'direction'];
      const LBL_KEYS = ['label', 'name', 'id', 'point', 'wp', 'waypoint'];
      latCol = headers.findIndex(h => LAT_KEYS.includes(h));
      lonCol = headers.findIndex(h => LON_KEYS.includes(h));
      hdgCol = headers.findIndex(h => HDG_KEYS.includes(h));
      lblCol = headers.findIndex(h => LBL_KEYS.includes(h));
      if (latCol === -1) latCol = 0;
      if (lonCol === -1) lonCol = 1;
    }

    const dataLines = isHeader ? lines.slice(1) : lines;
    const wps = [];
    let parseErrors = 0;

    dataLines.forEach((line, i) => {
      const parts = line.trim().split(sep);
      if (parts.length < 2) { parseErrors++; return; }

      const latRaw = parseFloat(parts[latCol]);
      const lonRaw = parseFloat(parts[lonCol]);
      if (isNaN(latRaw) || isNaN(lonRaw)) { parseErrors++; return; }

      // Auto-correct lon/lat swap
      let lat = latRaw, lon = lonRaw;
      if (Math.abs(latRaw) > 90 && Math.abs(lonRaw) <= 90) { lat = lonRaw; lon = latRaw; }

      const hdgRaw = hdgCol >= 0 && hdgCol < parts.length ? parts[hdgCol]?.trim() : '';
      const hdgNum = parseFloat(hdgRaw);
      const lblRaw = lblCol >= 0 && lblCol < parts.length ? parts[lblCol]?.trim() : '';

      wps.push({
        lat: lat.toFixed(6),
        lon: lon.toFixed(6),
        heading: (!isNaN(hdgNum) && hdgRaw !== '') ? String(hdgRaw) : '',
        label: lblRaw || `WP ${wps.length + 1}`,
      });
    });

    return wps.length > 0 ? { wps, parseErrors, total: dataLines.length } : null;
  }, []);

  // Re-parse on paste text change
  useEffect(() => {
    if (!pasteText.trim()) { setPastePreview(null); return; }
    const result = parsePasteText(pasteText);
    setPastePreview(result);
  }, [pasteText, parsePasteText]);

  const handleApplyPaste = useCallback(() => {
    if (!pastePreview?.wps?.length) return;
    if (pasteMode === 'replace') {
      setWaypoints(pastePreview.wps);
    } else {
      setWaypoints(prev => [...prev, ...pastePreview.wps]);
    }
    setPasteText('');
    setPastePreview(null);
    setShowPasteArea(false);
  }, [pastePreview, pasteMode]);

  // ── Stats ──────────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const valid = waypoints.filter(wp => {
      const lat = parseFloat(wp.lat), lon = parseFloat(wp.lon);
      return !isNaN(lat) && !isNaN(lon);
    });
    if (valid.length < 2) return null;
    const coords = valid.map(wp => [parseFloat(wp.lon), parseFloat(wp.lat)]);
    const totalDist = coords.reduce((sum, c, i) => i === 0 ? 0 : sum + haversineDistance(coords[i - 1], c), 0);
    const bearings = [];
    for (let i = 1; i < coords.length; i++) {
      const geo = calculateBearing(coords[i - 1], coords[i]);
      bearings.push({ geo, raw: geoBearingToRaw(geo, zeroRefDeg) });
    }
    return { valid: valid.length, totalDist, bearings };
  }, [waypoints, zeroRefDeg]);

  // ── Export GeoJSON ─────────────────────────────────────────────────────────
  const buildGeoJson = useCallback(() => {
    const valid = waypoints.filter(wp => {
      const lat = parseFloat(wp.lat), lon = parseFloat(wp.lon);
      return !isNaN(lat) && !isNaN(lon);
    });
    const features = [];

    // LineString
    if (valid.length >= 2) {
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: valid.map(wp => [parseFloat(wp.lon), parseFloat(wp.lat)]) },
        properties: { name: 'Route', pointCount: valid.length },
      });
    }

    // Points with headings
    valid.forEach((wp, i) => {
      const lat = parseFloat(wp.lat), lon = parseFloat(wp.lon);
      const rawHeading = parseFloat(wp.heading);
      const props = {
        name: wp.label || `WP ${i + 1}`,
        index: i,
      };
      if (!isNaN(rawHeading)) {
        props.heading = rawHeading;
        props.heading_reference = `${selectedPreset.label} (0° = ${zeroRefDeg}° from North)${applyMathTransform ? ', applied 360-|90-hdg|' : ''}`;
        props.geo_bearing = rawHeadingToGeoBearing(rawHeading, zeroRefDeg, applyMathTransform);
      }
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [lon, lat] },
        properties: props,
      });
    });

    return { type: 'FeatureCollection', features };
  }, [waypoints, zeroRefDeg, selectedPreset]);

  const handleExport = () => {
    exportGeoJSON(buildGeoJson(), 'geojson', 'route_with_headings');
  };

  const handleCopyGeoJson = () => {
    navigator.clipboard.writeText(JSON.stringify(buildGeoJson(), null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <SEO
        title="Route Drawer with Headings | GeoSpatial Tools | UmbrellaCo"
        description="Draw routes with per-waypoint headings and configure a custom zero-reference direction. Visualize directional arrows on an interactive map and export to GeoJSON."
        keywords="route drawer, heading, bearing, compass reference, geospatial, waypoints"
        url="/spatial-tools/route-drawer"
        jsonLd={createSoftwareAppSchema(
          'Route Drawer',
          'Draw routes with headings and configurable compass reference.',
          'https://umbrellaco.in/spatial-tools/route-drawer',
          ['Route Drawing', 'Heading Visualization', 'Compass Reference']
        )}
      />

      <Box sx={{ px: 2, pt: 1, pb: 0.5, flexShrink: 0 }}>
        <Typography component="h1" variant="h6" fontWeight={700}>Route Drawer</Typography>
        <Typography variant="body2" color="text.secondary">
          Draw routes with per-waypoint headings. Configure the zero-reference direction (North, East, or custom) to match your coordinate system.
        </Typography>
      </Box>

      <PanelGroup direction="horizontal">
        {/* ── Left Panel ── */}
        <Panel defaultSize={38} minSize={28}>
          <Box sx={{
            height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden',
            bgcolor: 'background.paper', borderRight: `1px solid ${theme.palette.divider}`,
          }}>

            {/* ── Zero Reference Section ── */}
            <Box sx={{ p: 2, flexShrink: 0 }}>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1.5 }}>
                <ExploreIcon fontSize="small" sx={{ color: 'primary.main' }} />
                <Typography variant="subtitle2" fontWeight={700}>Zero Reference Direction</Typography>
              </Stack>

              <Stack direction="row" spacing={2} alignItems="flex-start">
                {/* Compass Rose */}
                <Box sx={{ flexShrink: 0 }}>
                  <CompassRose zeroRefDeg={zeroRefDeg} />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
                    🟡 = your 0°
                  </Typography>
                </Box>

                {/* Controls */}
                <Box sx={{ flex: 1 }}>
                  <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                    <InputLabel>Reference</InputLabel>
                    <Select
                      value={zeroRefPreset}
                      label="Reference"
                      onChange={(e) => setZeroRefPreset(e.target.value)}
                    >
                      {ZERO_REF_PRESETS.map((p, i) => (
                        <MenuItem key={i} value={i}>{p.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {selectedPreset.value === 'custom' && (
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Custom offset: {customZeroRef}° from North (CW)
                      </Typography>
                      <Slider
                        value={customZeroRef}
                        onChange={(_, v) => setCustomZeroRef(v)}
                        min={0} max={359} step={1}
                        size="small"
                        sx={{ mt: 0.5 }}
                      />
                      <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                        {[0, 45, 90, 135, 180, 225, 270, 315].map(v => (
                          <Chip key={v} label={`${v}°`} size="small" variant={customZeroRef === v ? 'filled' : 'outlined'}
                            onClick={() => setCustomZeroRef(v)} sx={{ cursor: 'pointer', fontSize: '0.65rem' }} />
                        ))}
                      </Stack>
                    </Box>
                  )}

                  <Paper variant="outlined" sx={{ p: 1, bgcolor: 'action.hover' }}>
                    <Typography variant="caption" color="text.secondary">{selectedPreset.description}</Typography>
                    <Typography variant="caption" component="div" sx={{ mt: 0.25, fontFamily: 'monospace', fontSize: '0.7rem' }}>
                      0° user → {zeroRefDeg}° geographic bearing
                    </Typography>
                    <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                      90° user → {((90 + zeroRefDeg) % 360)}° geographic bearing
                    </Typography>
                  </Paper>
                  <FormControlLabel
                    control={<Switch size="small" checked={applyMathTransform} onChange={e => setApplyMathTransform(e.target.checked)} />}
                    label={<Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>Apply: 360 - |90 - hdg|</Typography>}
                    sx={{ mt: 0.5, ml: 0 }}
                  />
                </Box>
              </Stack>
            </Box>

            <Divider />

            {/* ── Display Options ── */}
            <Box sx={{ px: 2, py: 1, flexShrink: 0 }}>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
                <TuneIcon fontSize="small" sx={{ color: 'primary.main' }} />
                <Typography variant="subtitle2" fontWeight={700}>Display</Typography>
              </Stack>
              <Stack spacing={0.5}>
                <FormControlLabel
                  control={<Switch size="small" checked={showHeadingArrows} onChange={e => setShowHeadingArrows(e.target.checked)} />}
                  label={<Typography variant="body2">Show heading arrows at waypoints</Typography>}
                />
                <FormControlLabel
                  control={<Switch size="small" checked={showRouteArrows} onChange={e => setShowRouteArrows(e.target.checked)} />}
                  label={<Typography variant="body2">Show direction arrows along route</Typography>}
                />
                {showRouteArrows && (
                  <Box sx={{ px: 1 }}>
                    <Typography variant="caption" color="text.secondary">Arrow spacing: {arrowSpacing >= 1000 ? `${(arrowSpacing/1000).toFixed(1)} km` : `${arrowSpacing} m`}</Typography>
                    <Slider
                      value={arrowSpacing}
                      onChange={(_, v) => setArrowSpacing(v)}
                      min={100} max={5000} step={100}
                      size="small"
                    />
                  </Box>
                )}
              </Stack>
            </Box>

            <Divider />

            {/* ── Waypoints Table ── */}
            <Box sx={{ px: 2, pt: 1, pb: 0.5, flexShrink: 0 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <RouteIcon fontSize="small" sx={{ color: 'primary.main' }} />
                  <Typography variant="subtitle2" fontWeight={700}>Waypoints</Typography>
                  <Chip label={`${waypoints.length} pts`} size="small" variant="outlined" />
                </Stack>
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="Paste lat/lon/heading as text">
                    <Button
                      size="small" variant={showPasteArea ? 'contained' : 'outlined'}
                      startIcon={<ContentPasteIcon />}
                      onClick={() => setShowPasteArea(p => !p)}
                      sx={{ fontSize: '0.75rem', ...(showPasteArea && { background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }) }}
                    >
                      Paste
                    </Button>
                  </Tooltip>
                  <Tooltip title="Import from file (GeoJSON, CSV, GPX)">
                    <Button component="label" size="small" variant="outlined" startIcon={<UploadFileIcon />} sx={{ fontSize: '0.75rem' }}>
                      Import
                      <input type="file" hidden accept=".geojson,.json,.csv,.tsv,.gpx,.kml,.txt" onChange={handleImport} />
                    </Button>
                  </Tooltip>
                  <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={addWaypoint}
                    sx={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', fontSize: '0.75rem' }}>
                    Add
                  </Button>
                </Stack>
              </Stack>
              {error && <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError('')}>{error}</Alert>}
            </Box>

            {/* ── Paste Area ── */}
            {showPasteArea && (
              <Box sx={{ px: 2, pb: 1.5, flexShrink: 0, borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  One waypoint per line. Auto-detects separators (comma, tab, space, semicolon).
                  Columns: <code>lat, lon</code> or <code>lat, lon, heading</code> or <code>lat, lon, heading, label</code>.
                  Also accepts JSON arrays: <code>[[lat,lon,hdg],...]</code>
                </Typography>
                <TextField
                  multiline
                  rows={5}
                  fullWidth
                  size="small"
                  placeholder={`28.6139, 77.2090, 45, Start\n28.6500, 77.2500, 90\n28.7000, 77.3000`}
                  value={pasteText}
                  onChange={e => setPasteText(e.target.value)}
                  sx={{ fontFamily: 'monospace', fontSize: '0.8rem', mb: 1 }}
                  inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.78rem' } }}
                />

                {/* Preview */}
                {pastePreview && (
                  <Paper variant="outlined" sx={{
                    p: 1, mb: 1,
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(16,185,129,0.08)' : 'rgba(16,185,129,0.04)',
                    borderColor: '#10b981',
                  }}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <CheckIcon sx={{ fontSize: 14, color: '#10b981' }} />
                      <Typography variant="caption" color="#10b981" fontWeight={700}>
                        {pastePreview.wps.length} waypoints parsed
                        {pastePreview.parseErrors > 0 && ` (${pastePreview.parseErrors} lines skipped)`}
                      </Typography>
                    </Stack>
                    {pastePreview.wps.slice(0, 3).map((wp, i) => (
                      <Typography key={i} variant="caption" component="div" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'text.secondary' }}>
                        {wp.label}: {wp.lat}, {wp.lon}{wp.heading ? `, hdg=${wp.heading}°` : ''}
                      </Typography>
                    ))}
                    {pastePreview.wps.length > 3 && (
                      <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>
                        …and {pastePreview.wps.length - 3} more
                      </Typography>
                    )}
                  </Paper>
                )}

                {pasteText.trim() && !pastePreview && (
                  <Alert severity="warning" sx={{ mb: 1, py: 0 }}>
                    <Typography variant="caption">Could not parse input — check format</Typography>
                  </Alert>
                )}

                <Stack direction="row" spacing={1} alignItems="center">
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={pasteMode}
                    onChange={(_, v) => v && setPasteMode(v)}
                    sx={{ height: 28 }}
                  >
                    <ToggleButton value="append" sx={{ fontSize: '0.7rem', px: 1, py: 0 }}>Append</ToggleButton>
                    <ToggleButton value="replace" sx={{ fontSize: '0.7rem', px: 1, py: 0 }}>Replace</ToggleButton>
                  </ToggleButtonGroup>
                  <Button
                    size="small" variant="contained" fullWidth
                    disabled={!pastePreview?.wps?.length}
                    onClick={handleApplyPaste}
                    startIcon={<CheckIcon />}
                    sx={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', fontSize: '0.75rem' }}
                  >
                    Apply {pastePreview?.wps?.length ? `(${pastePreview.wps.length} WPs)` : ''}
                  </Button>
                </Stack>
              </Box>
            )}

            <WaypointsTable
              waypoints={waypoints}
              selectedPreset={selectedPreset}
              updateWaypoint={updateWaypoint}
              removeWaypoint={removeWaypoint}
            />

            {/* ── Stats ── */}
            {stats && (
              <>
                <Divider />
                <Box sx={{ px: 2, py: 1, flexShrink: 0 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                    {[
                      { label: 'Waypoints', value: stats.valid },
                      { label: 'Distance', value: stats.totalDist >= 1000 ? `${(stats.totalDist / 1000).toFixed(2)} km` : `${Math.round(stats.totalDist)} m` },
                    ].map(({ label, value }) => (
                      <Paper key={label} variant="outlined" sx={{
                        p: 1, textAlign: 'center',
                        background: theme.palette.mode === 'dark'
                          ? 'linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(139,92,246,0.08) 100%)'
                          : 'linear-gradient(135deg, rgba(99,102,241,0.04) 0%, rgba(139,92,246,0.04) 100%)',
                      }}>
                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                        <Typography variant="body2" fontWeight={700}>{value}</Typography>
                      </Paper>
                    ))}
                  </Box>
                  {stats.bearings.length > 0 && (
                    <Paper variant="outlined" sx={{ p: 1, mt: 1, fontFamily: 'monospace', fontSize: '0.7rem' }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>Segment Bearings</Typography>
                      {stats.bearings.map((b, i) => (
                        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.25 }}>
                          <span style={{ color: theme.palette.text.secondary }}>Seg {i + 1}:</span>
                          <span>{b.raw.toFixed(1)}° <span style={{ color: theme.palette.text.disabled }}>(→ {b.geo.toFixed(1)}° geo)</span></span>
                        </Box>
                      ))}
                    </Paper>
                  )}
                </Box>
              </>
            )}

            {/* ── Export ── */}
            <Divider />
            <Box sx={{ p: 1.5, flexShrink: 0 }}>
              <Stack direction="row" spacing={1}>
                <Button size="small" variant="contained" startIcon={<DownloadIcon />} onClick={handleExport}
                  sx={{ flex: 1, background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', fontSize: '0.75rem' }}>
                  Export GeoJSON
                </Button>
                <Tooltip title={copied ? 'Copied!' : 'Copy GeoJSON'}>
                  <IconButton size="small" onClick={handleCopyGeoJson} sx={{ border: `1px solid ${theme.palette.divider}` }}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
          </Box>
        </Panel>

        <PanelResizeHandle style={{ width: '5px', background: theme.palette.divider, cursor: 'col-resize' }} />

        {/* ── Right Panel (Map) ── */}
        <Panel defaultSize={62} minSize={35}>
          <Box sx={{ height: '100%', width: '100%' }}>
            <RouteDrawerMap
              waypoints={waypoints}
              zeroRefDeg={zeroRefDeg}
              applyMathTransform={applyMathTransform}
              showHeadingArrows={showHeadingArrows}
              showRouteArrows={showRouteArrows}
              arrowSpacing={arrowSpacing}
            />
          </Box>
        </Panel>
      </PanelGroup>
    </Box>
  );
}

export default RouteDrawer;
